import { z } from 'zod';
import { AccountStatus, AppRole } from './roles.js';
/**
 * The admin surface: who has access, who is waiting for it, and what everyone
 * has been doing. Every schema here backs a route under `/api/v1/admin`, which
 * is gated by `requireAdmin` at the *prefix* so an admin route cannot be added
 * unprotected by omission — security.md §4.
 */
/** One row of the admin user table. */
export const AdminUser = z
    .object({
    id: z.number().int().positive(),
    name: z.string(),
    email: z.string(),
    initials: z.string(),
    role: AppRole,
    status: AccountStatus,
    /** Who moved this account off `pending`, and when. Null while it is waiting. */
    approvedByName: z.string().nullable(),
    approvedAt: z.string().datetime({ offset: true }).nullable(),
    lastLoginAt: z.string().datetime({ offset: true }).nullable(),
    /** Newest `activity_log` entry for this user — the "last activity" column. */
    lastActivityAt: z.string().datetime({ offset: true }).nullable(),
    scenarioCount: z.number().int().min(0),
    createdAt: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * A role and/or status change. Both optional so the admin screen can approve
 * without touching role, or change role without re-approving, but at least one
 * must be present — a no-op PATCH should be a 400, not a silent success that
 * still writes an activity-log entry.
 */
export const UpdateUserBody = z
    .object({
    role: AppRole.optional(),
    status: AccountStatus.optional(),
})
    .strict()
    .refine((body) => body.role !== undefined || body.status !== undefined, {
    message: 'Provide at least one of role or status.',
});
/**
 * The events worth keeping. A closed list, not "whatever a caller sends":
 * an audit log whose vocabulary anything can extend is not auditable, and the
 * matching database CHECK constraint means an unknown kind fails at the write
 * rather than quietly polluting the feed.
 */
export const ActivityKind = z.enum([
    'signed_in',
    'scenario_created',
    'scenario_updated',
    'scenario_finalized',
    'scenario_deleted',
    'access_approved',
    'access_rejected',
    'access_revoked',
    'role_changed',
]);
export const ActivityEvent = z
    .object({
    id: z.number().int().positive(),
    kind: ActivityKind,
    /** Who did it. */
    actorUserId: z.number().int().positive(),
    actorName: z.string().nullable(),
    /** The account acted *upon*, for approvals and role changes. */
    targetUserId: z.number().int().positive().nullable(),
    targetName: z.string().nullable(),
    /**
     * The scenario acted upon. Denormalized name, and no foreign key: an audit
     * entry that disappears when its scenario is deleted is not an audit
     * entry, and "deleted scenario X" is exactly the row you most want to keep.
     */
    scenarioId: z.string().uuid().nullable(),
    scenarioName: z.string().nullable(),
    /** Small structured extras, e.g. `{ from: 'User', to: 'Admin' }`. */
    detail: z.record(z.string()).nullable(),
    createdAt: z.string().datetime({ offset: true }),
})
    .strict();
/** Keyset pagination on `id`, not an offset — the feed grows at the head. */
export const ActivityFeedQuery = z
    .object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    /** Return only events older than this id. */
    before: z.coerce.number().int().positive().optional(),
    userId: z.coerce.number().int().positive().optional(),
    kind: ActivityKind.optional(),
})
    .strict();
export const ActivityFeed = z
    .object({
    events: z.array(ActivityEvent),
    /** Pass back as `before` for the next page; null when the feed is exhausted. */
    nextBefore: z.number().int().positive().nullable(),
})
    .strict();
//# sourceMappingURL=admin.js.map